/**
 * Verification Error Handler Singleton
 * 
 * Provides a global handler for verification errors that can be used by the API client.
 * The handler is set by VerificationContext when it mounts.
 */

export interface VerificationErrorData {
  code: 'DATA_VERIFICATION_REQUIRED' | 'ID_VERIFICATION_REQUIRED';
  message: string;
  requiredVerifications: string[];
  currentStatus: {
    email: string;
    data: string;
    id?: string;
  };
}

type VerificationHandler = (error: any) => boolean;

let globalHandler: VerificationHandler | null = null;

/**
 * Set the global verification error handler.
 * Called by VerificationContext when it mounts.
 */
export const setGlobalVerificationHandler = (handler: VerificationHandler | null) => {
  globalHandler = handler;
};

/**
 * Handle a potential verification error.
 * Returns true if the error was a verification error and was handled.
 */
export const handleVerificationError = (error: any): boolean => {
  if (globalHandler) {
    return globalHandler(error);
  }
  return false;
};

/**
 * Check if an error is a verification error.
 */
export const isVerificationError = (error: any): boolean => {
  const errorData = error?.response?.data || error?.response || error;
  const errorCode = errorData?.code || errorData?.error;
  return errorCode === 'DATA_VERIFICATION_REQUIRED' || errorCode === 'ID_VERIFICATION_REQUIRED';
};

/**
 * Extract verification error data from an error object.
 */
export const extractVerificationErrorData = (error: any): VerificationErrorData | null => {
  const errorData = error?.response?.data || error?.response || error;
  const errorCode = errorData?.code || errorData?.error;
  
  if (errorCode === 'DATA_VERIFICATION_REQUIRED' || errorCode === 'ID_VERIFICATION_REQUIRED') {
    return {
      code: errorCode,
      message: errorData?.message || 'Verification required',
      requiredVerifications: errorData?.requiredVerifications || [],
      currentStatus: errorData?.currentStatus || {},
    };
  }
  
  return null;
};

